/*! *********************************************************************
 *
 * Copyright 2019 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *************************************************************************
 */

import React, { useState } from 'react';
import { useInput } from 'react-admin';
import _map from 'lodash/map';
import _remove from 'lodash/remove';
import { Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import TextureIcon from '@material-ui/icons/Texture';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';

import AssetsRefs from './AssetsRefs';
import AssetsSelect from './AssetsSelect';
import AssetsList from './AssetsList';
import AssetsGrid from './AssetsGrid';

const useStyles = makeStyles((theme) => ({
  addContainer: {
    marginBottom: '3em',
    marginTop: theme.spacing(2),
  },
  texture: {
    fontSize: '100px',
    opacity: 0.1,
  },
  helper: {
    opacity: 0.3,
  },
  orCaption: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

const Assets = (props) => {
  const [isGridView, setGridView] = useState(false);

  const viewChanged = (event) => {
    setGridView(event.target.checked);
  };

  const noSelected = props.numSelected === 0;

  return (
    <>
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        style={{
          height: '48px',
          borderRadius: '4px',
          backgroundColor: noSelected ? undefined : 'rgba(25, 118, 210, 0.12)',
        }}
      >
        <Grid item>
          <Box ml="16px">
            {noSelected ? (
              <Typography component="h2">Assets list</Typography>
            ) : (
              <Typography component="div">
                {props.numSelected} selected
              </Typography>
            )}
          </Box>
        </Grid>

        <Grid item>
          {noSelected ? (
            <FormControlLabel
              control={<Switch onChange={viewChanged} />}
              label="Grid View"
            />
          ) : (
            <IconButton onClick={props.onDeleteSelected}>
              <DeleteIcon />
            </IconButton>
          )}
        </Grid>
      </Grid>

      {isGridView ? <AssetsGrid {...props} /> : <AssetsList {...props} />}
    </>
  );
};

const AssetsView = (props) => {
  const {
    input: { value, onChange },
  } = useInput(props);
  const [selected, setSelected] = useState([]);
  const classes = useStyles();

  const populateCollectionAssets = () => {
    return _map(value, (asset, index) => {
      asset.index = index;
      return asset;
    });
  };

  const handleSelectAsset = (asset) => {
    addToCollection([asset]);
  };

  const handleChangePosition = (id, index) => {
    let assets = [...value];
    const removedAsset = _remove(assets, (a) => a.id === id);
    assets.splice(parseInt(index, 10), 0, removedAsset[0]);
    onChange(assets);
  };

  const handleDeleteAsset = (id) => {
    const assets = [...value];
    _remove(assets, (a) => a.id === id);
    onChange(assets);
  };

  const handleDeleteSelected = () => {
    const assets = [...value];
    selected.forEach((id) => _remove(assets, (a) => a.id === id));
    onChange(assets);
    setSelected([]);
  };

  const addToCollection = (newAssets) => {
    const assets = [...value];
    const updatedList = [].concat(newAssets, assets);
    onChange(updatedList);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      let assets = [...value];
      const newSelected = _map(assets, (a) => a.id);
      setSelected(newSelected);
    } else {
      setSelected([]);
    }
  };

  const handleSelectItem = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const assetsInList = populateCollectionAssets();
  const assetsInListLength = Object.keys(assetsInList).length;

  return (
    <>
      <Typography component="h2">Add Assets</Typography>
      <Grid container spacing={4} className={classes.addContainer}>
        <Grid item xs={4}>
          <AssetsSelect
            assetsInList={assetsInList}
            onChange={handleSelectAsset}
          />
        </Grid>
        <Grid item xs={1} center className={classes.orCaption}>
          <Typography variant="caption" display="block" gutterBottom>
            OR
          </Typography>
        </Grid>
        <Grid item xs={7}>
          <AssetsRefs
            assetsInList={assetsInList}
            assetsAdded={addToCollection}
          />
        </Grid>
      </Grid>

      {Object.keys(assetsInList).length < 1 ? (
        <Grid
          container
          direction="column"
          justify="space-between"
          alignItems="center"
        >
          <Grid item>
            <TextureIcon className={classes.texture} />
          </Grid>
          <Grid item>
            <Typography
              component="h2"
              variant="headline"
              className={classes.helper}
            >
              There is no assets defined
            </Typography>
          </Grid>
          <Grid item>
            <Typography
              component="h2"
              variant="subheading"
              className={classes.helper}
            >
              Plese select an asset in the field above
            </Typography>
          </Grid>
        </Grid>
      ) : (
        <Assets
          assets={assetsInList}
          assetsLength={assetsInListLength}
          handleChangePosition={handleChangePosition}
          handleDeleteAsset={handleDeleteAsset}
          onSelectAllClick={handleSelectAllClick}
          onSelectItem={handleSelectItem}
          isSelected={isSelected}
          numSelected={selected.length}
          onDeleteSelected={handleDeleteSelected}
          classes={classes}
        />
      )}
    </>
  );
};

export default AssetsView;
